import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { captureException } from "@freightsimple/observability/errorReporting";
import type { OpenClaimLineItem } from "@freightsimple/generated-dashboard-openapi-client/models/OpenClaimLineItem";
import type { OpenFreightClaimRequest } from "@freightsimple/generated-dashboard-openapi-client/models/OpenFreightClaimRequest";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import type { FreightClaim } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaim";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { FreightClaimItemCondition } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimItemCondition";
import { FreightClaimType } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimType";
import { FreightClaimRepairability } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimRepairability";
import { useReaction } from "@/Shared/Reactions/useReaction";
import { ReactionType } from "@/Shared/Reactions/ReactionType";
import { useShipmentService } from "@/Services/ShipmentService";
import { useFreightClaimsApi } from "@/Hooks/useFreightClaimsApi";
import { useOnce } from "@/Hooks/useOnce";
import { isNotFoundError } from "@/Helpers/isNotFoundError";

const MAX_TOTAL_FILE_SIZE_BYTES = 100 * 1024 * 1024;
export const MAX_TOTAL_FILE_SIZE_MB = 100;
const MAX_INVOICE_FILE_SIZE_BYTES = 25 * 1024 * 1024;

function emptyClaimLineItems(shipment: Shipment): OpenClaimLineItem[] {
  return shipment.lineItems.map(function () {
    return {
      claimType: FreightClaimType.NoDamageOrLoss,
      condition: FreightClaimItemCondition.New,
      repairability: FreightClaimRepairability.Unknown,
    };
  });
}

// All the submit-claim state, validation, and upload logic — the screen
// itself just lays out the form.
export function useClaimsSubmitClaim(shipmentId: string) {
  const shipmentsService = useShipmentService();
  const freightClaimsApi = useFreightClaimsApi();

  const [shipment, setShipment] = useState<Shipment | undefined>();
  const [shipmentNotFound, setShipmentNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [existingClaims, setExistingClaims] = useState<FreightClaim[]>([]);

  const [forceValidation, setForceValidation] = useState(false);

  const [originalInvoiceFiles, setOriginalInvoiceFiles] = useState<Array<File>>(
    [],
  );
  const [photosFiles, setPhotosFiles] = useState<Array<File>>([]);
  const [otherDocumentsFiles, setOtherDocumentsFiles] = useState<Array<File>>(
    [],
  );
  const [currency, setCurrency] = useState<Currency>(Currency.Cad);
  const [claimLineItems, setClaimLineItems] = useState<OpenClaimLineItem[]>([]);
  const [otherNotes, setOtherNotes] = useState("");
  const [description, setDescription] = useState("");
  const [openClaimErrorMessage, setOpenClaimErrorMessage] = useState<
    string | undefined
  >();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | undefined>();
  const { showReaction } = useReaction();
  const navigate = useNavigate();

  function validateFileSize(files: File[]): string | undefined {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);

    if (totalSize > MAX_TOTAL_FILE_SIZE_BYTES) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
      return `The total size of all files (${totalSizeMB}MB) exceeds the ${MAX_TOTAL_FILE_SIZE_MB}MB limit`;
    }

    return undefined;
  }

  const validateInvoiceFilesSize = (files: File[]) => {
    const invoicesFilesSize = files.reduce((sum, file) => sum + file.size, 0);

    if (invoicesFilesSize > MAX_INVOICE_FILE_SIZE_BYTES) {
      return "The selected files may not be invoices. Please verify and try again.";
    }

    return undefined;
  };

  async function loadShipment() {
    setLoadFailed(false);
    try {
      const response = await shipmentsService.getShipment(shipmentId);

      if (!response.shipment) {
        setShipmentNotFound(true);
        return;
      }

      setShipment(response.shipment);
      setClaimLineItems(emptyClaimLineItems(response.shipment));

      // Load existing claims
      try {
        const claims = await freightClaimsApi.getByShipmentId({
          shipmentId,
        });
        setExistingClaims(claims);
      } catch (e) {
        console.warn("Failed to load existing claims:", e);
        setExistingClaims([]);
      }
    } catch (e) {
      if (isNotFoundError(e)) {
        setShipmentNotFound(true);
      } else {
        console.warn("Error loading shipment:", { e });
        setLoadFailed(true);
      }
    }
  }

  useOnce(async () => {
    loadShipment();
  });

  function isClaimLineItemValid(claimLineItem: OpenClaimLineItem): boolean {
    if (claimLineItem.claimType === FreightClaimType.NoDamageOrLoss) {
      return true;
    }

    if (
      claimLineItem.amountPerPiece === undefined ||
      claimLineItem.amountPerPiece < 0.01
    ) {
      return false;
    }

    if (
      claimLineItem.affectedPieces === undefined ||
      claimLineItem.affectedPieces === 0
    ) {
      return false;
    }

    if (
      claimLineItem.totalPieces === undefined ||
      claimLineItem.totalPieces === 0
    ) {
      return false;
    }

    if (claimLineItem.affectedPieces > claimLineItem.totalPieces) {
      return false;
    }

    return true;
  }

  function isValid(): boolean {
    if (originalInvoiceFiles.length === 0) {
      return false;
    }

    const allFiles = [
      ...originalInvoiceFiles,
      ...photosFiles,
      ...otherDocumentsFiles,
    ];

    if (validateFileSize(allFiles)) {
      return false;
    }

    if (validateInvoiceFilesSize(originalInvoiceFiles)) {
      return false;
    }

    if (
      claimLineItems.every(
        (o) => o.claimType === FreightClaimType.NoDamageOrLoss,
      )
    ) {
      return false;
    }

    if (claimLineItems.some((o) => !isClaimLineItemValid(o))) {
      return false;
    }

    if (description.trim() === "") {
      return false;
    }

    return true;
  }

  const allFiles = [
    ...originalInvoiceFiles,
    ...photosFiles,
    ...otherDocumentsFiles,
  ];
  const fileSizeErrorMessage = validateFileSize(allFiles);

  const invoiceFileSizeErrorMessage =
    validateInvoiceFilesSize(originalInvoiceFiles);

  const generalErrorMessage = !isValid()
    ? fileSizeErrorMessage || "Please correct the above errors"
    : undefined;

  const descriptionErrorMessage = description.trim() === "" ? "Required" : "";

  const originalInvoiceErrorMessage =
    originalInvoiceFiles.length === 0
      ? "Required"
      : invoiceFileSizeErrorMessage || undefined;

  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  async function onSubmitClaim() {
    setIsUploading(true);
    setForceValidation(true);
    setOpenClaimErrorMessage(undefined);
    setUploadStatus(undefined);

    if (!isValid()) {
      setIsUploading(false);
      return;
    }

    try {
      const request: OpenFreightClaimRequest = {
        shipmentId,
        description,
        otherNotes,
        claimLineItems,
        currency,
      };

      console.log("Starting freight claim upload", {
        shipmentId,
        claimLineItemsCount: claimLineItems.length,
      });

      setUploadStatus("Uploading files...");

      await freightClaimsApi.upload(
        request,
        originalInvoiceFiles,
        photosFiles,
        otherDocumentsFiles,
        (percentComplete) => {
          if (percentComplete >= 100) {
            setUploadStatus("Processing your claim...");
          } else {
            setUploadStatus(`Uploading files... ${percentComplete}%`);
          }
        },
      );

      console.log("Freight claim upload successful", { shipmentId });
      showReaction(ReactionType.Success);
      navigate(`/view-shipment?shipmentId=${shipmentId}`);
    } catch (e) {
      console.error("Error submitting freight claim:", {
        error: e,
        shipmentId,
        isAxiosError: axios.isAxiosError(e),
        errorCode: axios.isAxiosError(e) ? e.code : undefined,
        errorMessage: axios.isAxiosError(e) ? e.message : undefined,
        responseStatus: axios.isAxiosError(e) ? e.response?.status : undefined,
        responseData: axios.isAxiosError(e) ? e.response?.data : undefined,
      });

      if (!axios.isAxiosError(e)) {
        setOpenClaimErrorMessage(
          "Sorry. Something went wrong. Please try again or contact support if the problem persists.",
        );
      } else {
        // Handle specific error cases
        if (e.response?.status === 413) {
          setOpenClaimErrorMessage(
            "One or more files you are trying to upload are too large. Please try uploading fewer or smaller files, or contact support for assistance.",
          );
        } else {
          captureException(e, {
            component: "SubmitClaimScreen",
            action: "submitClaim",
            shipmentId,
            currency,
            "axios.status": String(e.response?.status ?? ""),
            "axios.code": String(e.code ?? ""),
            "axios.message": e.message ?? "",
          });
          setOpenClaimErrorMessage(
            "Sorry. Something went wrong while submitting your claim. Please try again or contact support if the problem persists.",
          );
        }
      }
    } finally {
      setIsUploading(false);
      setUploadStatus(undefined);
    }
  }

  return {
    shipment,
    shipmentNotFound,
    loadFailed,
    retryLoad: loadShipment,
    existingClaims,
    forceValidation,
    originalInvoiceFiles,
    setOriginalInvoiceFiles,
    photosFiles,
    setPhotosFiles,
    otherDocumentsFiles,
    setOtherDocumentsFiles,
    currency,
    setCurrency,
    claimLineItems,
    setClaimLineItems,
    otherNotes,
    setOtherNotes,
    description,
    setDescription,
    openClaimErrorMessage,
    isUploading,
    uploadStatus,
    isValid,
    generalErrorMessage,
    descriptionErrorMessage,
    originalInvoiceErrorMessage,
    onSubmitClaim,
  };
}
